import type {
	ContractCostBreakDto,
	ContractPriceBreakDto,
} from '@supply-mover-2-0/shared';

export type ContractPriceBreak = Pick<
	ContractPriceBreakDto,
	'minimumQuantity' | 'price'
>;

/** Map of itemId → array of price breaks, as returned by getContractPricesForShopItems */
export type ContractPriceMap = Record<number, Array<ContractPriceBreak>>;

export type ContractCostBreak = Pick<
	ContractCostBreakDto,
	'minimumQuantity' | 'acquisitionCost' | 'sellCost'
>;

/** Map of itemId → array of cost breaks, as returned by getContractCostsForShopItems */
export type ContractCostMap = Record<number, Array<ContractCostBreak>>;

/**
 * Find the applicable contract price for an item at a given quantity.
 * Extracts the breaks for the item from the map, sorts ascending by
 * minimumQuantity, and returns the highest qualifying tier's price.
 */
export function getApplicableContractPrice(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	quantity: number,
): number | null {
	const breaks = contractPrices?.[itemId];

	if (breaks == null || breaks.length === 0) {
		return null;
	}

	const sorted = [...breaks].sort(
		(a, b) => a.minimumQuantity - b.minimumQuantity,
	);
	let applicablePrice: number | null = null;

	for (const brk of sorted) {
		if (quantity >= brk.minimumQuantity) {
			applicablePrice = brk.price;
		}
	}

	return applicablePrice;
}

/**
 * Find the applicable contract cost break for an item at a given quantity.
 * Mirrors getApplicableContractPrice: sorts the item's breaks ascending by
 * minimumQuantity and returns the highest qualifying tier. Returns null when no
 * break qualifies (e.g. the lowest tier's minimumQuantity exceeds `quantity`),
 * which the caller treats as "inherit the item's own cost." Each break may carry
 * a null acquisitionCost or sellCost individually (that dimension is not
 * contract-overridden), so callers must null-check per dimension too.
 */
export function getApplicableContractCost(
	contractCosts: ContractCostMap | null | undefined,
	itemId: number,
	quantity: number,
): ContractCostBreak | null {
	const breaks = contractCosts?.[itemId];

	if (breaks == null || breaks.length === 0) {
		return null;
	}

	const sorted = [...breaks].sort(
		(a, b) => a.minimumQuantity - b.minimumQuantity,
	);
	let applicable: ContractCostBreak | null = null;

	for (const brk of sorted) {
		if (quantity >= brk.minimumQuantity) {
			applicable = brk;
		}
	}

	return applicable;
}

/**
 * Check if a price matches the applicable contract price for an item/quantity.
 * Uses tolerance-based comparison to handle floating point precision.
 */
export function isPriceFromContract(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	price: number,
	quantity: number,
): boolean {
	const contractPrice = getApplicableContractPrice(
		contractPrices,
		itemId,
		quantity,
	);

	return contractPrice != null && Math.abs(price - contractPrice) < 0.001;
}

/**
 * Get the effective display price for an item.
 * Returns the contract price if one applies, otherwise the list price.
 */
export function getEffectiveItemPrice(
	contractPrices: ContractPriceMap | null | undefined,
	itemId: number,
	quantity: number,
	listPrice: number,
): number {
	return (
		getApplicableContractPrice(contractPrices, itemId, quantity) ?? listPrice
	);
}

export interface ResolveEffectiveMinimumPriceOptions {
	/**
	 * The item's own `minimumPrice` column. Decimal columns arrive as strings
	 * from the database, so both shapes are accepted. An empty string is treated
	 * as "not set", the same as null.
	 */
	itemMinimumPrice: string | number | null | undefined;
	/**
	 * Whether the `instance-feature-item.defaultMinimumPrice` setting applies to
	 * the user (or channel) the minimum is being resolved for. The caller
	 * resolves it, because which preference set applies depends on who is
	 * acting (see PreferencesService.isDefaultMinimumPriceEnabled).
	 */
	defaultMinimumPriceEnabled: boolean;
	/**
	 * The client's contract price breaks, as returned by
	 * getContractPricesForShopItems for that client (saved prices are the
	 * priority-0 contract, so they arrive through the same map).
	 */
	contractPrices: ContractPriceMap | null | undefined;
	itemId: number;
	quantity: number;
	/** The item's list price (`item.price`). */
	listPrice: string | number | null | undefined;
}

/**
 * Parse a decimal price column (strings from the database, or numbers) into a
 * finite number. Null, an empty string and anything unparsable are "not set"
 * and return null; 0 is a real value and is returned as 0.
 */
export const parseOptionalPrice = (
	value: string | number | null | undefined,
): number | null => {
	if (value == null) {
		return null;
	}

	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	if (value.trim() === '') {
		return null;
	}

	const parsed = parseFloat(value);

	return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Resolve the minimum price that applies to an order line (PD-16173).
 *
 * Mirrors the legacy CRM's `_getItemMinimumPrice`:
 *  1. An item's own minimum price always wins, including an explicit 0. The
 *     setting never overrides a value someone deliberately stored.
 *  2. Otherwise, when the default-minimum-price setting is off, there is no
 *     minimum (null), which is the behavior every tenant without the setting
 *     keeps.
 *  3. Otherwise the minimum is the price the shop shows in its price column for
 *     this client, item and quantity: the applicable contract price (saved
 *     price or other contract, tier chosen by quantity), else the list price,
 *     else 0.
 *
 * The legacy CRM prefers a saved price over a cheaper contract under the
 * LOWEST_PRICE pricing rule, while Workd 2.0 shows the lowest. The owner chose
 * to follow the price Workd 2.0 displays, so the minimum always equals the
 * price the rep sees.
 */
export function resolveEffectiveMinimumPrice(
	options: ResolveEffectiveMinimumPriceOptions,
): number | null {
	const ownMinimum = parseOptionalPrice(options.itemMinimumPrice);

	if (ownMinimum != null) {
		return ownMinimum;
	}

	if (!options.defaultMinimumPriceEnabled) {
		return null;
	}

	const contractPrice = getApplicableContractPrice(
		options.contractPrices,
		options.itemId,
		options.quantity,
	);

	if (contractPrice != null) {
		return contractPrice;
	}

	return parseOptionalPrice(options.listPrice) ?? 0;
}
